import React from 'react';
import { useLocation, Navigate, Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Download } from 'lucide-react';
import SiteLayout, { PALETTE } from './SiteLayout';
import SEO from '../../components/SEO';
import { NewsletterSignup } from '../../components/NewsletterSignup';
import { splitLangPath, getDict, localizeDeep, langHref, MARKETING_LANGS } from '../../i18n/marketing';
import {
  LANDING_PAGES_BY_SLUG,
  ORG_JSONLD,
  LOCAL_BUSINESS_JSONLD,
  buildFAQ,
  buildBreadcrumb,
  buildSoftwareApp,
  SITE_URL,
} from '../../seo/landingConfig';
import { pickScreenshotsForSlug, pickInternalLinksForSlug } from '../../seo/landingVisuals';

/**
 * SeoLandingPage, keyword-targeted landing page rendered from
 * `LANDING_PAGES_BY_SLUG[slug]`. One component, many routes. Every page
 * ships full per-route meta + Organization + LocalBusiness + FAQPage +
 * BreadcrumbList + SoftwareApplication structured data.
 */
export default function SeoLandingPage() {
  const { pathname } = useLocation();
  const { lang, path } = splitLangPath(pathname);
  const slug = path.replace(/^\/+|\/+$/g, '');
  const dict = lang !== 'en' ? getDict(lang) : null;
  const baseCfg = LANDING_PAGES_BY_SLUG[slug];
  const cfg = baseCfg && dict ? localizeDeep(baseCfg, dict) : baseCfg;
  if (!cfg) return <Navigate to="/" replace />;

  // Keep visitors inside their language for links that have localized versions.
  const lHref = (to) => {
    if (lang === 'en') return to;
    const target = to.replace(/^\//, '');
    if (to.startsWith('/guides') || LANDING_PAGES_BY_SLUG[target]) return langHref(lang, to);
    return to;
  };

  const pagePath = langHref(lang, `/${cfg.slug}`);
  const url = `${SITE_URL}${pagePath}`;
  const alternates = [
    { hreflang: 'en', href: `${SITE_URL}/${cfg.slug}` },
    ...MARKETING_LANGS.map((l) => ({ hreflang: l, href: `${SITE_URL}/${l}/${cfg.slug}` })),
    { hreflang: 'x-default', href: `${SITE_URL}/${cfg.slug}` },
  ];
  const jsonLd = [
    ORG_JSONLD,
    LOCAL_BUSINESS_JSONLD,
    buildSoftwareApp({ name: cfg.title, description: cfg.metaDescription, url }),
    buildBreadcrumb(cfg.breadcrumbs),
    cfg.faqs && cfg.faqs.length ? buildFAQ(cfg.faqs) : null,
  ].filter(Boolean);

  return (
    <SiteLayout>
      <SEO
        title={cfg.metaTitle}
        description={cfg.metaDescription}
        path={pagePath}
        keywords={cfg.keywords}
        lang={lang}
        alternates={alternates}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section
        data-testid={`seo-hero-${cfg.slug}`}
        style={{
          background: PALETTE.cream,
          padding: '120px 32px 80px',
          borderBottom: `1px solid ${PALETTE.border}`,
        }}
      >
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" style={{ fontSize: 12, color: PALETTE.muted, marginBottom: 22 }}>
            {cfg.breadcrumbs.map((c, i) => (
              <span key={c.url}>
                {i > 0 && <span style={{ margin: '0 8px', color: PALETTE.border }}>/</span>}
                {i === cfg.breadcrumbs.length - 1 ? (
                  <span style={{ color: PALETTE.ink, fontWeight: 600 }}>{c.name}</span>
                ) : (
                  <Link to={c.url} style={{ color: PALETTE.muted, textDecoration: 'none' }}>{c.name}</Link>
                )}
              </span>
            ))}
          </nav>

          <div style={{ fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 18 }}>
            {cfg.eyebrow}
          </div>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(34px, 4.6vw, 60px)',
            fontWeight: 700,
            color: PALETTE.ink,
            margin: 0,
            letterSpacing: '-0.02em',
            lineHeight: 1.05,
          }}>
            {cfg.h1}<br />
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>{cfg.h1Italic}</em>
          </h1>
          <p style={{
            fontSize: 18,
            lineHeight: 1.65,
            color: PALETTE.slate,
            marginTop: 24,
            maxWidth: 780,
          }}>
            {cfg.lead}
          </p>

          {cfg.proofBullets && cfg.proofBullets.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '36px 0 0', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px 28px' }}>
              {cfg.proofBullets.map((b) => (
                <li key={b} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 14.5, color: PALETTE.ink, lineHeight: 1.5 }}>
                  <CheckCircle2 size={18} strokeWidth={2} color={PALETTE.magenta} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          <div style={{ marginTop: 40, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {cfg.downloadCta && (
              <a
                href={cfg.downloadCta.href}
                download
                data-testid={`seo-download-${cfg.slug}`}
                style={{
                  background: PALETTE.magenta,
                  color: '#fff',
                  padding: '14px 26px',
                  fontSize: 14,
                  fontWeight: 700,
                  textDecoration: 'none',
                  borderRadius: 4,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <Download size={16} /> {cfg.downloadCta.label}
              </a>
            )}
            <Link
              to="/site/contact"
              data-testid={`seo-cta-primary-${cfg.slug}`}
              style={{
                background: PALETTE.ink,
                color: '#fff',
                padding: '14px 26px',
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
                borderRadius: 4,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              Book a discovery call <ArrowRight size={16} />
            </Link>
            <Link
              to="/site/pricing"
              style={{
                background: 'transparent',
                color: PALETTE.ink,
                padding: '14px 26px',
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
                border: `1px solid ${PALETTE.border}`,
                borderRadius: 4,
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              How we price
            </Link>
          </div>
        </div>
      </section>

      {/* SCREENSHOT BAND, shows what the software actually looks like on
          every landing page. Auto-picked by slug keyword so a "mock-recall"
          page shows the recall screens, a "dispatch note" page shows
          dispatch screens, etc. */}
      {(() => {
        const shots = pickScreenshotsForSlug(cfg.slug);
        if (!shots || shots.length === 0) return null;
        return (
          <section
            data-testid={`seo-screenshots-${cfg.slug}`}
            style={{
              background: PALETTE.paper,
              padding: '72px 32px 88px',
              borderBottom: `1px solid ${PALETTE.border}`,
            }}
          >
            <div style={{ maxWidth: 1080, margin: '0 auto' }}>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 12 }}>
                See it in action
              </div>
              <h2 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(24px, 3.0vw, 34px)',
                fontWeight: 700,
                color: PALETTE.ink,
                margin: 0,
                letterSpacing: '-0.018em',
                lineHeight: 1.15,
                maxWidth: 720,
              }}>
                Real screens, from our Public Sandbox.
              </h2>
              <p style={{ fontSize: 15, color: PALETTE.slate, marginTop: 14, maxWidth: 720, lineHeight: 1.6 }}>
                Every screenshot below is captured from the Public Sandbox demo tenant with sample Irish dairy data. No mockups, no client data, no NDAs.
              </p>
              <div style={{
                marginTop: 36,
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(shots.length, 3)}, minmax(0, 1fr))`,
                gap: 20,
              }} className="seo-shots-grid">
                {shots.map((s, i) => (
                  <figure key={s.src} style={{ margin: 0, background: '#fff', border: `1px solid ${PALETTE.border}`, borderRadius: 6, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <img
                      src={s.src}
                      alt={s.alt}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      width="1200"
                      height="750"
                      style={{ width: '100%', height: 'auto', display: 'block', borderBottom: `1px solid ${PALETTE.border}` }}
                    />
                    <figcaption style={{ padding: '14px 16px', fontSize: 13, color: PALETTE.slate, lineHeight: 1.55 }}>
                      {s.caption}
                    </figcaption>
                  </figure>
                ))}
              </div>
              <style>{`
                @media (max-width: 900px) {
                  .seo-shots-grid { grid-template-columns: 1fr !important; }
                }
              `}</style>
            </div>
          </section>
        );
      })()}
      {cfg.sections.map((section, i) => {
        const priorChecklistItems = cfg.sections
          .slice(0, i)
          .reduce((n, s) => n + (s.checklist || []).length, 0);
        return (
        <section
          key={i}
          style={{
            background: i % 2 === 0 ? '#fff' : PALETTE.paper,
            padding: '90px 32px',
            borderBottom: `1px solid ${PALETTE.border}`,
          }}
        >
          <div style={{ maxWidth: 1080, margin: '0 auto' }}>
            <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>
              {section.eyebrow}
            </div>
            <h2 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(26px, 3.4vw, 42px)',
              fontWeight: 700,
              color: PALETTE.ink,
              margin: 0,
              letterSpacing: '-0.018em',
              lineHeight: 1.1,
              maxWidth: 820,
            }}>
              {section.h2}
            </h2>

            {section.body && (
              <p style={{ fontSize: 16, lineHeight: 1.7, color: PALETTE.slate, marginTop: 22, maxWidth: 780 }}>
                {section.body}
              </p>
            )}

            {section.bullets && (
              <div style={{ marginTop: 36, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 }}>
                {section.bullets.map((b) => (
                  <div key={b.t} style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, borderLeft: `3px solid ${PALETTE.gold}`, padding: '18px 22px' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: PALETTE.ink, marginBottom: 6 }}>{b.t}</div>
                    <div style={{ fontSize: 13.5, color: PALETTE.muted, lineHeight: 1.55 }}>{b.d}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Numbered checklist items (audit-readiness checklist page), numbering runs globally across sections (1–18) */}
            {section.checklist && (
              <div style={{ marginTop: 36, display: 'grid', gap: 12 }}>
                {section.checklist.map((item, ci) => {
                  const n = priorChecklistItems + ci + 1;
                  return (
                  <div key={item.t} data-testid={`checklist-item-${n}`} style={{ display: 'flex', gap: 16, background: '#fff', border: `1px solid ${PALETTE.border}`, padding: '16px 20px', alignItems: 'flex-start' }}>
                    <div style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 4, background: PALETTE.ink, color: PALETTE.gold, fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {String(n).padStart(2, '0')}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: PALETTE.ink }}>
                        {item.t}
                        {item.ref && <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 600, color: PALETTE.magenta, letterSpacing: '0.04em' }}>{item.ref}</span>}
                      </div>
                      <div style={{ fontSize: 13.5, color: PALETTE.slate, lineHeight: 1.6, marginTop: 4 }}>{item.d}</div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {/* Opt-in newsletter block (email course) */}
            {section.newsletter && (
              <div style={{ marginTop: 36, maxWidth: 720 }}>
                <NewsletterSignup source={section.newsletter.source || cfg.slug} />
              </div>
            )}

            {section.comparisonTable && (
              <div style={{ marginTop: 36, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: `1px solid ${PALETTE.border}` }}>
                  <thead>
                    <tr style={{ background: PALETTE.ink, color: '#fff' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, letterSpacing: '0.05em', fontWeight: 700 }}>Capability</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, letterSpacing: '0.05em', fontWeight: 700, color: '#94A3B8' }}>Excel</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, letterSpacing: '0.05em', fontWeight: 700, background: PALETTE.magenta }}>Keystone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.comparisonTable.map((r, idx) => (
                      <tr key={r.capability} style={{ background: idx % 2 ? PALETTE.cream : '#fff', borderTop: `1px solid ${PALETTE.border}` }}>
                        <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: PALETTE.ink }}>{r.capability}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: PALETTE.muted }}>✗ {r.excel}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: '#16A34A', fontWeight: 600, background: '#F0FDF4' }}>✓ {r.keystone}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Multi-competitor comparison table (Keystone + N competitors) */}
            {section.competitorTable && (
              <div style={{ marginTop: 36, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: `1px solid ${PALETTE.border}`, minWidth: 720 }}>
                  <thead>
                    <tr style={{ background: PALETTE.ink, color: '#fff' }}>
                      <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: 11.5, letterSpacing: '0.05em', fontWeight: 700, minWidth: 200 }}>Feature</th>
                      {section.competitorTable.competitors.map((c, i) => (
                        <th key={c} style={{
                          padding: '12px 14px',
                          textAlign: 'left',
                          fontSize: 11.5,
                          letterSpacing: '0.05em',
                          fontWeight: 700,
                          background: i === 0 ? PALETTE.magenta : 'transparent',
                          color: i === 0 ? '#fff' : '#94A3B8',
                          minWidth: 140,
                        }}>
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.competitorTable.rows.map((r, idx) => (
                      <tr key={r.feature} style={{ background: idx % 2 ? PALETTE.cream : '#fff', borderTop: `1px solid ${PALETTE.border}`, verticalAlign: 'top' }}>
                        <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: PALETTE.ink }} dangerouslySetInnerHTML={{ __html: r.feature.replace(/\(/g, '<br/><span style="font-weight:400;font-size:11.5px;color:#5B6B82">(').replace(/\)/g, ')</span>') }} />
                        {r.values.map((v, i) => {
                          const isKeystone = i === 0;
                          const hasTick = typeof v === 'string' && v.trim().startsWith('✓');
                          return (
                            <td key={i} style={{
                              padding: '12px 14px',
                              fontSize: 12.5,
                              color: isKeystone ? '#16A34A' : PALETTE.muted,
                              fontWeight: isKeystone ? 600 : 400,
                              background: isKeystone ? '#F0FDF4' : 'transparent',
                              borderLeft: isKeystone ? `2px solid ${PALETTE.magenta}` : 'none',
                            }}>
                              {!isKeystone && !hasTick && v && v !== 'No' ? <span style={{ color: PALETTE.muted }}>{v}</span> : v}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Year-1 TCO cards */}
            {section.pricingTable && (
              <div style={{ marginTop: 36, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16 }}>
                {section.pricingTable.map((p) => {
                  const highlight = p.tone === 'highlight';
                  return (
                    <div key={p.name} style={{
                      background: highlight ? PALETTE.ink : '#fff',
                      color: highlight ? '#fff' : PALETTE.ink,
                      border: `1px solid ${highlight ? PALETTE.gold : PALETTE.border}`,
                      borderRadius: 6,
                      padding: '22px 22px 20px',
                      position: 'relative',
                      ...(highlight ? { boxShadow: '0 8px 24px rgba(10,37,64,0.18)' } : {}),
                    }}>
                      {highlight && (
                        <div style={{
                          position: 'absolute',
                          top: -10,
                          left: 22,
                          background: PALETTE.gold,
                          color: PALETTE.ink,
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '3px 9px',
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          borderRadius: 2,
                        }}>Best value · Irish food</div>
                      )}
                      <div style={{ fontSize: 16, fontWeight: 700, color: highlight ? '#fff' : PALETTE.ink }}>{p.name}</div>
                      <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: highlight ? PALETTE.gold : PALETTE.muted, marginTop: 2, marginBottom: 14, fontWeight: 600 }}>{p.tag}</div>
                      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 32, fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1, color: highlight ? '#fff' : PALETTE.ink }}>{p.headline}</div>
                      <div style={{ fontSize: 11.5, color: highlight ? '#94A3B8' : PALETTE.muted, marginTop: 4 }}>{p.sub}</div>
                      <div style={{ fontSize: 12.5, color: highlight ? '#CBD5E1' : PALETTE.slate, lineHeight: 1.55, marginTop: 14, minHeight: 70 }}>{p.detail}</div>
                      <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${highlight ? 'rgba(255,255,255,0.15)' : PALETTE.border}` }}>
                        <div style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: highlight ? PALETTE.gold : PALETTE.muted, fontWeight: 700 }}>Year-1 TCO</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: highlight ? PALETTE.gold : PALETTE.magenta, marginTop: 2, fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: '-0.01em' }}>{p.tco}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
        );
      })}

      {/* INTERNAL LINK STRIP, mid-page card row of related landing pages.
          Boosts SEO internal linking and gives users a quick side-step to
          adjacent topics. Auto-derived from the topic family if not
          explicitly set on cfg.internalLinks. */}
      {(() => {
        const links = cfg.internalLinks && cfg.internalLinks.length
          ? cfg.internalLinks
          : pickInternalLinksForSlug(cfg.slug, LANDING_PAGES_BY_SLUG);
        if (!links || links.length === 0) return null;
        return (
          <section
            data-testid={`seo-link-strip-${cfg.slug}`}
            style={{
              background: PALETTE.paper,
              padding: '72px 32px',
              borderBottom: `1px solid ${PALETTE.border}`,
            }}
          >
            <div style={{ maxWidth: 1080, margin: '0 auto' }}>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>
                Keep reading
              </div>
              <h2 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(22px, 2.6vw, 30px)',
                fontWeight: 700,
                color: PALETTE.ink,
                margin: '0 0 28px',
                letterSpacing: '-0.018em',
                lineHeight: 1.15,
              }}>
                Related pages on this stack.
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(links.length, 4)}, minmax(0, 1fr))`,
                gap: 14,
              }} className="seo-link-strip-grid">
                {links.map((l) => (
                  <Link
                    key={l.to}
                    to={lHref(l.to)}
                    data-testid={`seo-strip-link-${cfg.slug}-${l.to.replace(/[^a-z0-9]+/gi, '-')}`}
                    style={{
                      background: '#fff',
                      border: `1px solid ${PALETTE.border}`,
                      borderLeft: `3px solid ${PALETTE.gold}`,
                      padding: '18px 18px 20px',
                      textDecoration: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 132,
                      transition: 'border-color 120ms ease, transform 120ms ease',
                    }}
                  >
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: PALETTE.ink, lineHeight: 1.35 }}>
                      {l.label}
                    </div>
                    {l.blurb && (
                      <div style={{ fontSize: 12.5, color: PALETTE.muted, marginTop: 8, lineHeight: 1.5, flex: 1 }}>
                        {String(l.blurb).slice(0, 130)}{String(l.blurb).length > 130 ? '…' : ''}
                      </div>
                    )}
                    <div style={{ fontSize: 12, fontWeight: 700, color: PALETTE.magenta, marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                      Read <ArrowRight size={14} />
                    </div>
                  </Link>
                ))}
              </div>
              <style>{`
                @media (max-width: 900px) {
                  .seo-link-strip-grid { grid-template-columns: 1fr 1fr !important; }
                }
                @media (max-width: 560px) {
                  .seo-link-strip-grid { grid-template-columns: 1fr !important; }
                }
              `}</style>
            </div>
          </section>
        );
      })()}

      {/* FAQ */}
      {cfg.faqs && cfg.faqs.length > 0 && (
        <section style={{ background: '#fff', padding: '100px 32px', borderBottom: `1px solid ${PALETTE.border}` }}>
          <div style={{ maxWidth: 880, margin: '0 auto' }}>
            <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>
              FAQ
            </div>
            <h2 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(26px, 3.2vw, 38px)',
              fontWeight: 700,
              color: PALETTE.ink,
              margin: '0 0 36px',
              letterSpacing: '-0.018em',
              lineHeight: 1.1,
            }}>
              Questions buyers actually ask.
            </h2>
            <div style={{ display: 'grid', gap: 16 }}>
              {cfg.faqs.map((f) => (
                <details key={f.q} style={{ background: PALETTE.paper, border: `1px solid ${PALETTE.border}`, borderRadius: 4, padding: '16px 20px' }}>
                  <summary style={{ cursor: 'pointer', fontSize: 15.5, fontWeight: 700, color: PALETTE.ink, listStyle: 'none' }}>
                    {f.q}
                  </summary>
                  <div style={{ marginTop: 12, fontSize: 14.5, color: PALETTE.slate, lineHeight: 1.65 }}>
                    {f.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA + RELATED */}
      <section style={{ background: PALETTE.ink, color: '#fff', padding: '80px 32px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48 }} className="cta-grid">
          <div>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(24px, 3.0vw, 36px)', fontWeight: 700, margin: 0, color: '#fff', lineHeight: 1.1 }}>
              Ready to see if Keystone fits your floor?
            </h2>
            <p style={{ fontSize: 15, color: '#CBD5E1', marginTop: 18, lineHeight: 1.65 }}>
              20-minute discovery call. No sales pitch. Written scope within 48 hours if we fit, referral to someone better if we don&apos;t.
            </p>
            <Link to="/site/contact" data-testid={`seo-cta-bottom-${cfg.slug}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 22, padding: '14px 26px', background: PALETTE.magenta, color: '#fff', fontSize: 14, fontWeight: 700, textDecoration: 'none', borderRadius: 4 }}>
              Talk to us <ArrowRight size={16} />
            </Link>
          </div>
          {cfg.relatedLinks && (
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 16 }}>
                Related reading
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {cfg.relatedLinks.map((r) => (
                  <li key={r.to} style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <Link to={lHref(r.to)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', color: '#fff', textDecoration: 'none', fontSize: 15 }}>
                      {r.label}
                      <ArrowRight size={16} color={PALETTE.gold} />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <style>{`
          @media (max-width: 760px) {
            .cta-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          }
        `}</style>
      </section>
    </SiteLayout>
  );
}
